import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { TablesInsert, TablesUpdate } from "~/api/tables";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { db } from "~/api/db";
import {
  parseRecurringLineItems,
  requireText,
  rootKey,
  workspaceKey,
  textOrNull,
  useWorkspaceMutation,
  type InvoiceReminder,
  type RecurringInvoice,
} from "./shared";
export function useWorkspaceRecurringInvoices(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("recurring-invoices", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as RecurringInvoice[];
      const { data, error } = await db
        .from("recurring_invoices")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("next_issue_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as RecurringInvoice[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "recurring-invoices",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const dueDays = Number(input.dueDays);
      const maxOccurrences = Number(input.maxOccurrences);
      const lineItems = parseRecurringLineItems(input.lineItems);
      const { error } = await db.from("recurring_invoices").insert({
        workspace_id: targetWorkspaceId,
        client_id: textOrNull(input.clientId),
        title: requireText(input.title, "Template title"),
        cadence: requireText(
          input.cadence,
          "Cadence",
        ) as RecurringInvoice["cadence"],
        next_issue_date: requireText(input.nextIssueDate, "Next issue date"),
        due_days: Number.isFinite(dueDays) && dueDays >= 0 ? dueDays : 14,
        ends_on: textOrNull(input.endsOn),
        enabled: true,
        max_occurrences:
          Number.isInteger(maxOccurrences) && maxOccurrences > 0
            ? maxOccurrences
            : null,
        template: {
          line_items: lineItems,
          notes: textOrNull(input.notes),
          payment_instructions: textOrNull(input.paymentInstructions),
        },
      } satisfies TablesInsert<"recurring_invoices">);
      if (error) throw error;
    },
  );

  const setEnabled = useWorkspaceMutation(
    workspaceId,
    "recurring-invoices",
    async (input: { id: string; enabled: boolean }) => {
      const { error } = await db
        .from("recurring_invoices")
        .update({ enabled: input.enabled })
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const update = useWorkspaceMutation(
    workspaceId,
    "recurring-invoices",
    async (input: { id: string; values: Record<string, string> }) => {
      const dueDays = Number(input.values.dueDays);
      const maxOccurrences = Number(input.values.maxOccurrences);
      const lineItems = parseRecurringLineItems(input.values.lineItems);
      const { error } = await db
        .from("recurring_invoices")
        .update({
          cadence: requireText(
            input.values.cadence,
            "Cadence",
          ) as RecurringInvoice["cadence"],
          client_id: textOrNull(input.values.clientId),
          due_days: Number.isFinite(dueDays) && dueDays >= 0 ? dueDays : 14,
          ends_on: textOrNull(input.values.endsOn),
          max_occurrences:
            Number.isInteger(maxOccurrences) && maxOccurrences > 0
              ? maxOccurrences
              : null,
          next_issue_date: requireText(
            input.values.nextIssueDate,
            "Next issue date",
          ),
          template: {
            line_items: lineItems,
            notes: textOrNull(input.values.notes),
            payment_instructions: textOrNull(input.values.paymentInstructions),
          },
          title: requireText(input.values.title, "Template title"),
        } satisfies TablesUpdate<"recurring_invoices">)
        .eq("id", input.id);
      if (error) throw error;
    },
  );

  const generateDue = useMutation({
    mutationFn: async (input?: { recurringInvoiceId?: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      void input;
      return unwrap(
        api.runRecurringInvoices({ body: { workspace_id: workspaceId } }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKey("recurring-invoices", workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  return { ...query, create, generateDue, setEnabled, update };
}

export function useWorkspaceInvoiceReminders(
  workspaceId: string | undefined,
  invoiceIds: string[],
) {
  const queryClient = useQueryClient();
  const invoiceKey = invoiceIds.join(",");
  const query = useQuery({
    queryKey: [...rootKey, "invoice-reminders", workspaceId ?? "", invoiceKey],
    queryFn: async () => {
      if (!workspaceId || invoiceIds.length === 0)
        return [] as InvoiceReminder[];
      const { data, error } = await db
        .from("invoice_reminders")
        .select("*")
        .in("invoice_id", invoiceIds)
        .order("scheduled_for", { ascending: true });
      if (error) throw error;
      return (data ?? []) as InvoiceReminder[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useMutation({
    mutationFn: async (input: { invoiceId: string; scheduledFor: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoiceId)) {
        throw new Error("Choose an invoice in this workspace.");
      }
      const date = requireText(input.scheduledFor, "Reminder date");
      const { error } = await db.from("invoice_reminders").insert({
        invoice_id: input.invoiceId,
        scheduled_for: `${date}T09:00:00+13:00`,
        status: "scheduled",
      } satisfies TablesInsert<"invoice_reminders">);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          ...rootKey,
          "invoice-reminders",
          workspaceId ?? "",
          invoiceKey,
        ],
      });
    },
  });

  const cancel = useMutation({
    mutationFn: async (reminderId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("invoice_reminders")
        .update({ status: "cancelled" })
        .eq("id", reminderId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          ...rootKey,
          "invoice-reminders",
          workspaceId ?? "",
          invoiceKey,
        ],
      });
    },
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; scheduledFor: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const date = requireText(input.scheduledFor, "Reminder date");
      const { error } = await db
        .from("invoice_reminders")
        .update({ scheduled_for: `${date}T09:00:00+13:00` })
        .eq("id", input.id)
        .eq("status", "scheduled");
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          ...rootKey,
          "invoice-reminders",
          workspaceId ?? "",
          invoiceKey,
        ],
      });
    },
  });

  const sendDue = useMutation({
    mutationFn: async (input?: { invoiceId?: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      void input;
      return unwrap(
        api.sendInvoiceReminders({ body: { workspace_id: workspaceId } }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ...rootKey,
            "invoice-reminders",
            workspaceId ?? "",
            invoiceKey,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  return {
    ...query,
    reminders: query.data ?? [],
    cancel,
    create,
    sendDue,
    update,
  };
}
